import { type ReactNode } from 'react';
import { ReservationDetailDto } from '@/services/platform/api/model';
import {
  useLocaleStore,
  formatDate,
  formatTimeFromDate,
  formatDateTimeFromDate,
  parseText,
  useClientTranslation,
} from '@/lib/core';
import { useReservationSummaryLabel } from '@/services/platform/data/booking/reservationSummary';

// ── SummaryRow ────────────────────────────────────────────────────────────────

/**
 * `label` is display text, not an i18n key. It used to be the latter, resolved here by
 * `` `App.Labels.${capitalise(label)}` as TranslationKey `` — a cast that checked nothing, over a
 * prop that three call sites were already passing translated copy into. Resolving at the call site
 * instead makes those correct by construction and lets the Activity screen share the same key map.
 */
function SummaryRow({ label, value, type }: { label: string; value: ReactNode; type: string }) {
  return (
    <div className="border-jncquoi-neutral-200 flex items-start gap-8 border-b pb-4" data-type={type}>
      <span className="font-pp-neue-montreal w-35 shrink-0 text-[12px] tracking-[0.5px] text-black uppercase">
        {label}
      </span>
      <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 flex-1 text-right text-[14px] tracking-[0.5px]">
        {typeof value === 'string' ? parseText(value) : value}
      </span>
    </div>
  );
}

// ── BookingDetailSummary ──────────────────────────────────────────────────────

interface BookingDetailSummaryProps {
  reservation: ReservationDetailDto;
  bookingSummaryLabel?: string;
}

export function BookingDetailSummary({ reservation, bookingSummaryLabel }: BookingDetailSummaryProps) {
  const locale = useLocaleStore(state => state.locale);
  const t = useClientTranslation();
  const summaryLabel = useReservationSummaryLabel();

  const serviceType = reservation.serviceType ?? 'Other';
  const venueName = reservation.venue?.name ?? reservation.name;
  const category = reservation.serviceCategory?.name;

  const startDate = formatDate(reservation.startDate, locale);
  const startTime = formatTimeFromDate(reservation.startDate);
  const endDate = formatDate(reservation.endDate, locale);
  const endTime = formatTimeFromDate(reservation?.endDateTime ?? reservation?.endDate);

  const guests = reservation.guestCount;
  const adults = reservation.adultsCount;
  const children = reservation.childrenCount;

  const reservations = reservation.reservations;
  const addons = reservation.reservations?.[0]?.products;

  const summaryItems =
    serviceType === 'Driver' && reservation.summaryItems
      ? [...reservation.summaryItems].sort(
          (a, b) => Number(a.key?.toLowerCase() === 'when') - Number(b.key?.toLowerCase() === 'when')
        )
      : reservation.summaryItems;

  return (
    <div className="flex flex-col gap-6">
      {bookingSummaryLabel && (
        <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] tracking-[0.5px] uppercase">
          {bookingSummaryLabel}
        </p>
      )}
      <div className="flex flex-col gap-4">
        {summaryItems && summaryItems.length > 0 ? (
          summaryItems.map((item, idx) => {
            const key = item.key?.toLowerCase();
            let value = item.value ?? '';

            if (key === 'when') {
              if (serviceType === 'Sunbed' || serviceType === 'Stay') {
                if (startDate !== endDate) {
                  value = `${[startDate, startTime].filter(Boolean).join(', ')} <br/> ${[endDate, endTime].filter(Boolean).join(', ')}`;
                } else if (startTime !== endTime) {
                  value = `${[startDate, startTime].filter(Boolean).join(', ')} - ${[endTime].filter(Boolean).join(', ')}`;
                } else {
                  value = [startDate, startTime].filter(Boolean).join(', ');
                }
              } else {
                value =
                  serviceType === 'Driver' && reservation?.status?.toLowerCase() != 'checkedout'
                    ? `${t('App.Labels.ScheduleRide')}<br/>${[startDate, category, startTime].filter(Boolean).join(', ')}`
                    : [startDate, category, startTime].filter(Boolean).join(', ');
              }
            } else if (key === 'howmany') {
              if (serviceType === 'Sunbed' || serviceType === 'Stay') {
                if ((adults ?? 0) > 0) {
                  value = `${adults} ${adults === 1 ? t('App.Labels.SunbedAdult') : t('App.Labels.SunbedAdults')}`;
                }
                if ((children ?? 0) > 0) {
                  value += ` + ${children} ${children === 1 ? t('App.Labels.SunbedChildren') : t('App.Labels.SunbedChildren')}`;
                }
              } else if (serviceType === 'Parking') {
                value = `${value} ${parseInt(value) && parseInt(value) === 1 ? t('App.Labels.ParkingSpace') : t('App.Labels.ParkingSpaces')}`;
              } else {
                value = `${value} ${parseInt(value) && parseInt(value) === 1 ? t('App.Labels.Guest') : t('App.Labels.Guests')}`;
              }
            } else if (key === 'checkin') {
              value = formatDateTimeFromDate(value);
            }

            return <SummaryRow key={idx} label={summaryLabel(item.key)} value={value} type={serviceType} />;
          })
        ) : (
          <>
            {venueName && <SummaryRow label={t('App.Labels.Where')} value={venueName} type={serviceType} />}
            {startDate && (
              <SummaryRow
                label={t('App.Labels.When')}
                value={[startDate, category, startTime].filter(Boolean).join(', ')}
                type={serviceType}
              />
            )}
            {guests != null && (
              <SummaryRow label={t('App.Labels.HowMany')} value={`${guests}`} type={serviceType} />
            )}
            {reservation.comments && (
              <SummaryRow label={t('App.Labels.Comment')} value={reservation.comments} type={serviceType} />
            )}
          </>
        )}
        <>
          {reservations && (
            <SummaryRow
              label={t('App.Labels.SunbedSummaryWhichCategory')}
              value={
                <div className="flex flex-col gap-3">
                  {reservations.map((r, i) => (
                    <div key={i} className="flex flex-col">
                      <span>{`x1 ${r?.categoryName}`}</span>
                      <span className="text-jncquoi-neutral-700">
                        ({`${t('App.Labels.SunbedAdults')}: ${r?.adultCount}`}
                        {r?.childCount && `, ${t('App.Labels.SunbedChildren')}: ${r?.childCount ?? 0}`})
                      </span>
                    </div>
                  ))}
                </div>
              }
              type={serviceType}
            />
          )}
          {addons && (
            <SummaryRow
              label={t('App.Labels.Addons')}
              value={
                <div className="flex flex-col gap-1">
                  {addons
                    .filter(a => a.name)
                    .map((a, i) => (
                      <span key={i}>{a.name}</span>
                    ))}
                </div>
              }
              type={serviceType}
            />
          )}
        </>
      </div>
    </div>
  );
}
